/**
 * Employee Validations
 * Business rules and field validations for employees
 */

import { EmployeeFormData, EmployeeStatus } from '../types/employee';
import { hojeEmSaoPaulo } from './hojeEmSaoPaulo';

/**
 * Mesma trava que o servidor aplica entre situação e data de demissão, para o erro
 * aparecer no campo antes do envio. Devolve os erros por campo; vazio quando coerente.
 */
export const validateSituacaoComDemissao = (
  status: EmployeeStatus | '' | undefined,
  dismissalDate: string | undefined,
  hoje: string = hojeEmSaoPaulo()
): Record<string, string> => {
  const dia = dismissalDate?.slice(0, 10) ?? '';
  const demitido = status === EmployeeStatus.DEMITIDO;

  if (dia !== '' && !demitido) {
    return { status: 'Com data de demissão preenchida, a situação precisa ser Demitido' };
  }
  if (dia === '' && demitido) {
    return {
      dismissalDate:
        'Informe a data de demissão; para trazer o funcionário de volta, use Reativar',
    };
  }
  if (dia > hoje) {
    return { dismissalDate: 'A data de demissão não pode ser depois de hoje' };
  }
  return {};
};

/**
 * Validate CPF
 */
export const validateCPF = (cpf: string): boolean => {
  if (!cpf) return false;
  
  // Remove formatting
  const cleaned = cpf.replace(/\D/g, '');
  
  // Check length
  if (cleaned.length !== 11) return false;
  
  // Check if all digits are the same
  if (/^(\d)\1{10}$/.test(cleaned)) return false;
  
  // Validate first digit
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleaned.charAt(i)) * (10 - i);
  }
  let digit = 11 - (sum % 11);
  if (digit >= 10) digit = 0;
  if (digit !== parseInt(cleaned.charAt(9))) return false;
  
  // Validate second digit
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleaned.charAt(i)) * (11 - i);
  }
  digit = 11 - (sum % 11);
  if (digit >= 10) digit = 0;
  if (digit !== parseInt(cleaned.charAt(10))) return false;
  
  return true;
};

/**
 * Validate email
 */
export const validateEmail = (email: string): boolean => {
  if (!email) return false;
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

/**
 * Validate phone number (10 or 11 digits)
 */
export const validatePhone = (phone: string): boolean => {
  if (!phone) return false;
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length === 10 || cleaned.length === 11;
};

/**
 * Validate CEP (8 digits)
 */
export const validateCEP = (cep: string): boolean => {
  if (!cep) return false;
  const cleaned = cep.replace(/\D/g, '');
  return cleaned.length === 8;
};

/**
 * Validate PIS/PASEP
 */
export const validatePIS = (pis: string): boolean => {
  if (!pis) return false;
  
  const cleaned = pis.replace(/\D/g, '');
  
  if (cleaned.length !== 11) return false;
  
  // Check if all digits are the same
  if (/^(\d)\1{10}$/.test(cleaned)) return false;
  
  // Validate check digit
  const weights = [3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleaned.charAt(i)) * weights[i];
  }
  
  let digit = 11 - (sum % 11);
  if (digit >= 10) digit = 0;
  
  return digit === parseInt(cleaned.charAt(10));
};

/**
 * Validate CTPS (7 digits)
 */
export const validateCTPS = (ctps: string): boolean => {
  if (!ctps) return false;
  const cleaned = ctps.replace(/\D/g, '');
  return cleaned.length === 7;
};

/**
 * Validate birth date (must be in the past, not too old)
 */
export const validateBirthDate = (date: string | Date): { valid: boolean; message?: string } => {
  if (!date) {
    return { valid: false, message: 'Data de nascimento é obrigatória' };
  }
  
  const birthDate = typeof date === 'string' ? new Date(date) : date;
  const today = new Date();
  
  if (isNaN(birthDate.getTime())) {
    return { valid: false, message: 'Data inválida' };
  }
  
  if (birthDate >= today) {
    return { valid: false, message: 'Data de nascimento deve ser anterior à data atual' };
  }
  
  const age = today.getFullYear() - birthDate.getFullYear();
  
  if (age > 120) {
    return { valid: false, message: 'Data de nascimento inválida (idade acima de 120 anos)' };
  }
  
  if (age < 14) {
    return { valid: false, message: 'Funcionário deve ter pelo menos 14 anos' };
  }
  
  return { valid: true };
};

/**
 * Validate admission date
 */
export const validateAdmissionDate = (
  admissionDate: string | Date,
  birthDate?: string | Date
): { valid: boolean; message?: string } => {
  if (!admissionDate) {
    return { valid: false, message: 'Data de admissão é obrigatória' };
  }
  
  const admission = typeof admissionDate === 'string' ? new Date(admissionDate) : admissionDate;
  const today = new Date();
  
  if (isNaN(admission.getTime())) {
    return { valid: false, message: 'Data inválida' };
  }
  
  if (admission > today) {
    return { valid: false, message: 'Data de admissão não pode ser futura' };
  }
  
  // Validate against birth date if provided
  if (birthDate) {
    const birth = typeof birthDate === 'string' ? new Date(birthDate) : birthDate;
    const ageAtAdmission = admission.getFullYear() - birth.getFullYear();
    
    if (ageAtAdmission < 14) {
      return { 
        valid: false, 
        message: 'Funcionário deve ter pelo menos 14 anos na data de admissão' 
      };
    }
  }
  
  return { valid: true };
};

/**
 * Validate termination date
 */
export const validateTerminationDate = (
  terminationDate: string | Date,
  admissionDate?: string | Date
): { valid: boolean; message?: string } => {
  if (!terminationDate) {
    return { valid: true }; // Termination date is optional
  }
  
  const termination = typeof terminationDate === 'string' ? new Date(terminationDate) : terminationDate;
  
  if (isNaN(termination.getTime())) {
    return { valid: false, message: 'Data inválida' };
  }
  
  // Validate against admission date if provided
  if (admissionDate) {
    const admission = typeof admissionDate === 'string' ? new Date(admissionDate) : admissionDate;
    
    if (termination < admission) {
      return { 
        valid: false, 
        message: 'Data de demissão deve ser posterior à data de admissão' 
      };
    }
  }
  
  return { valid: true };
};

/**
 * Validate salary (must be > 0)
 */
export const validateSalary = (salary: number | string): { valid: boolean; message?: string } => {
  if (!salary) {
    return { valid: false, message: 'Salário é obrigatório' };
  }
  
  const value = typeof salary === 'string' ? parseFloat(salary) : salary;
  
  if (isNaN(value) || value <= 0) {
    return { valid: false, message: 'Salário deve ser maior que zero' };
  }
  
  return { valid: true };
};

/**
 * Validate employee form data
 */
export const validateEmployeeForm = (data: EmployeeFormData): Record<string, string> => {
  const errors: Record<string, string> = {};
  
  // Required fields
  if (!data.name?.trim()) {
    errors.name = 'Nome completo é obrigatório';
  }
  
  if (!data.cpf) {
    errors.cpf = 'CPF é obrigatório';
  } else if (!validateCPF(data.cpf)) {
    errors.cpf = 'CPF inválido';
  }
  
  if (!data.birthDate) {
    errors.birthDate = 'Data de nascimento é obrigatória';
  } else {
    const birthValidation = validateBirthDate(data.birthDate);
    if (!birthValidation.valid) {
      errors.birthDate = birthValidation.message!;
    }
  }
  
  if (!data.gender) {
    errors.gender = 'Sexo é obrigatório';
  }
  
  if (!data.companyId) {
    errors.companyId = 'Empresa é obrigatória';
  }
  
  // Optional but validatable fields
  if (data.email && !validateEmail(data.email)) {
    errors.email = 'E-mail inválido';
  }
  
  if (data.whatsapp && !validatePhone(data.whatsapp)) {
    errors.whatsapp = 'WhatsApp inválido (deve ter 10 ou 11 dígitos)';
  }
  
  if (data.zipCode && !validateCEP(data.zipCode)) {
    errors.zipCode = 'CEP inválido (deve ter 8 dígitos)';
  }
  
  if (data.pis && !validatePIS(data.pis)) {
    errors.pis = 'PIS/PASEP inválido';
  }
  
  if (data.ctps && !validateCTPS(data.ctps)) {
    errors.ctps = 'CTPS inválida (deve ter 7 dígitos)';
  }

  Object.assign(errors, validateSituacaoComDemissao(data.status, data.dismissalDate));

  return errors;
};

/**
 * Check if employee can be minor (age < 18)
 */
export const canBeMinor = (birthDate: string | Date): boolean => {
  const birth = typeof birthDate === 'string' ? new Date(birthDate) : birthDate;
  const today = new Date();
  const age = today.getFullYear() - birth.getFullYear();
  return age < 18;
};

/**
 * Validate disability percentage
 */
export const validateDisabilityPercentage = (percentage: number): { valid: boolean; message?: string } => {
  if (percentage < 0 || percentage > 100) {
    return { valid: false, message: 'Percentual deve estar entre 0 e 100' };
  }
  return { valid: true };
};

/**
 * Get required fields for employee form
 */
export const getRequiredFields = (): string[] => {
  return [
    'name',
    'cpf',
    'birthDate',
    'gender',
    'companyId'
  ];
};

/**
 * Check if all required fields are filled
 */
export const hasRequiredFields = (data: Partial<EmployeeFormData>): boolean => {
  const required = getRequiredFields();
  return required.every(field => {
    const value = data[field as keyof EmployeeFormData];
    return value !== null && value !== undefined && value !== '';
  });
};
